import { useAuth } from "../auth";
import { Button } from "@/components/ui/button";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel } from "@/components/ui/form"
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form"
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { UserSchema } from "@/components/user/UserSchema";
import { useTranslate } from "@/helper/translate";
import { useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import Icon from '@/img/spotlight_logo.webp';

export function LoginPage()
{
  const { translate } = useTranslate();
  
  const { login } = useAuth();
    const [errorText, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
  
  const form = useForm<z.infer<typeof UserSchema>>({
    resolver: zodResolver(UserSchema)
  });

  const onSubmit = async(values: z.infer<typeof UserSchema>) => {
     try {
          setError("");
          
           await login(form.getValues("userName"), form.getValues("newPassword")??"")

        } catch (err) {
          const errorCode = err instanceof Error ? err.message : "";
          const errorKeys: Record<string, string> = {
            LOGIN_INVALID_CREDENTIALS: "auth.errors.invalidCredentials",
            LOGIN_FORBIDDEN: "auth.errors.forbidden",
            LOGIN_TOO_MANY_ATTEMPTS: "auth.errors.tooManyAttempts",
            LOGIN_SERVER_ERROR: "auth.errors.server",
            LOGIN_FAILED: "auth.errors.failed",
          };
          setError(translate(errorKeys[errorCode] ?? "auth.errors.generic"));
        }
  }


  useEffect(() => {
    document.body.classList.add("login");
    
    return () => {
      document.body.classList.remove("login");
    };
  }, []);


  return (

<div className="flex justify-center items-center min-h-screen">

    <div className="bg-sky-50/10 md:w-1/4 xs:w-3/4 p-10 mx-auto rounded-md border border-solid border-white/35 backdrop-blur-sm">

      <h1 className="text-7xl text-center font-bold p-2 mb-5 bg-transparent flex justify-center"><img className="max-w-[70%]" src={Icon}/></h1>
      <Form {...form}>
           <form onSubmit={form.handleSubmit(onSubmit, (error) => { console.log("Validation errors:", error); })} className="space-y-2">
          <FormField
            control={form.control}
            name="userName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input 
                    {...field} 
                    className="bg-white"
                    type="text"
                    placeholder={translate("auth.email")}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Input 
                      {...field}
                      type={showPassword ? "text" : "password"}
                      className="bg-white pr-10"
                      placeholder={translate("auth.pwd")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900 w-[26px] h-[26px] p-[3px]"
                      aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          {errorText && <div className="text-red-500 text-center">{errorText}</div>}
          <Button className="w-full !text-white !bg-sky-950 hover:!bg-sky-900 !border-sky-950 hover:!border-sky-900" type="submit">{translate("auth.login")}</Button>
        </form>
      </Form>
    </div>

  <div className="fixed bottom-2 p-2">
    <p className="text-[10px]/[16px] text-center text-white">{translate("auth.copyright")}<br/>
      <a href="//redlof-medien.de/datenschutz" target="_blank" className="uppercase p-2">{translate("auth.privacy")}</a>
      <a href="//redlof-medien.de/impressum" target="_blank" className="uppercase p-2">{translate("auth.legal")}</a>
    </p>
  </div>

</div>
  )
}